import React, { useRef } from "react";
import { useState } from "react";
import { useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Grid, Box, CssBaseline } from "@mui/material";

import DetailsPageHeader from "../../../../common/DetailsPageHeader";
import { mainLayoutStyles, showNotification, routes } from "../../../../constants";
import Sidebar from "../../../../common/Sidebar";
import ImagePreviewModal from "../../../../common/ImagePreviewModal";
import AudioModal from "../../../../common/AudioModal";
import ConfirmationModal from "../../../../common/ConfirmationModal";
import { httpsCallable } from 'firebase/functions';
import { functions, promoteBatchQuicktale, db, auth } from '../../../../firebase';
import {
  QTDetailsDataFieldsSection,
  QTDetailsInteractionsFieldsSection,
} from "../../../OrganizationQuickTales/components/components";
import {
  getSingleBatchQuickTale,
  getSingleUser,
  updateBatchQuickTale,
  uploadQuickTaleAudioToStorage,
  uploadQuickTaleImageToStorage,
  uploadProcessedAudioToStorage,
  useShareActivityEvents,
} from "../../hooks/BatchQuickTalesHooks";
import {
  createCustomNarrator,
  narratorNameExists,
  getCustomNarratorsByUser,
  getCustomNarratorByName,
  getCustomNarratorByNameAndOrganization,
  getCustomNarratorByNameUnscoped,
  updateCustomNarrator,
} from "../../../OrganizationQuickTales/hooks/CustomNarratorsHooks";
import { resolveCreatedByData } from "../../../../utils/createdByUtils";
import { useGetEmailTemplates } from "../../../OrganizationEmailTemplates/hooks/EmailTemplatesHooks";

import { useQuickTalesContext } from "../../../../providers/OrganizationAdmin/QuickTalesProvider";
import { useOrganizationContext } from "../../../../providers/OrganizationAdmin/OrganizationProvider";
import { Toast } from "../../../../common/alert";
import { useAuth } from "../../../../hooks";
import { GeoPoint, serverTimestamp } from "firebase/firestore";
import moment from "moment";
import QTDetailsTextFieldWithLabelSection from "../../../../common/QTDetailsTextFieldWithLabelSection";
import ShareActivityTable from "../../components/ShareActivityTable/ShareActivityTable";

const BatchQuickTaleDetailsContainer = () => {
  const { currentUser } = useAuth();
  const DEBUG_CUSTOM_NARRATOR_LOOKUP = true;
  const {
    loading,
    setLoading,
  } = useQuickTalesContext();
  const { currentOrganization } = useOrganizationContext();
  const location = useLocation();
  const navigate = useNavigate();
  const isSaving = useRef(false);
  const { batchQuicktaleId: paramBatchQuicktaleId } = useParams(); // Get ID from URL params
  const [quicktaleDetails, setQuicktaleDetails] = useState(
    location?.state?.quicktaleDetails?.id
      ? location?.state?.quicktaleDetails
      : {}
  );
  const [imagePreviewModal, setImagePreviewModal] = useState(false);
  const [audioModal, setAudioModal] = useState(false);
  const [processedAudioModal, setProcessedAudioModal] = useState(false);
  const [editEnabled, setEditEnabled] = useState(false);
  const [initialApprovalStatus, setInitialApprovalStatus] = useState(
    location?.state?.quicktaleDetails?.approvalStatus?.status || null
  );
  const [uploadImage, setUploadImage] = useState(null);
  const [uploadAudioFile, setUploadAudioFile] = useState(null);
  const [uploadProcessedAudioFile, setUploadProcessedAudioFile] = useState(null);
  const [cancelConfirmationModalOpen, setCancelConfirmationModalOpen] = useState(false);
  const [emailPromptModalOpen, setEmailPromptModalOpen] = useState(false);
  const [narratorEmail, setNarratorEmail] = useState("");
  const [narratorFirstName, setNarratorFirstName] = useState("");
  const [narratorLastName, setNarratorLastName] = useState("");
  const [customNarratorDoc, setCustomNarratorDoc] = useState(null);
  const [getLinkModalOpen, setGetLinkModalOpen] = useState(false);
  const [generatedLink, setGeneratedLink] = useState("");
  const [isCopied, setIsCopied] = useState(false);
  const [narratorError, setNarratorError] = useState(false);

  // (Area UI moved into QTDetailsDataFieldsSection)
  
  // Email template selection states
  const [templateSelectionModalOpen, setTemplateSelectionModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [pendingNarratorData, setPendingNarratorData] = useState(null); // Stores narrator data while template is being selected

  // Get Link template selection states
  const [getLinkTemplateSelectionModalOpen, setGetLinkTemplateSelectionModalOpen] = useState(false);
  const [selectedGetLinkTemplate, setSelectedGetLinkTemplate] = useState("");
  
  // Fetch email templates for the organization
  const { templates: allEmailTemplates, loading: templatesLoading } = useGetEmailTemplates(quicktaleDetails?.organization);
  
  // Filter to only show active templates
  const activeEmailTemplates = allEmailTemplates?.filter(t => t.status === "active") || [];

  // When we touch a customNarrator record in batch flows, persist its org for future shared lookups.
  const customNarratorOrganizationId = quicktaleDetails?.organization || "";

  // Share activity tracking — real-time listener for the subcollection
  const { events: shareActivityEvents, loading: shareActivityLoading } = useShareActivityEvents(quicktaleDetails?.id);

  // Auto-select default email template (or single active template) when a template
  // selection modal is opened and no template has been chosen yet.
  useEffect(() => {
    if (!(templateSelectionModalOpen || emailPromptModalOpen)) return;
    if (!activeEmailTemplates || activeEmailTemplates.length === 0) return;
    if (selectedTemplate) return; // Don't override user choice

    const defaultTemplate = activeEmailTemplates.find((t) => t.default === true);
    if (defaultTemplate) {
      setSelectedTemplate(defaultTemplate.id);
    } else if (activeEmailTemplates.length === 1) {
      setSelectedTemplate(activeEmailTemplates[0].id);
    }
  }, [
    templateSelectionModalOpen,
    emailPromptModalOpen,
    activeEmailTemplates,
    selectedTemplate,
  ]);

  // Auto-select default email template (or single active template) when the Get Link
  // template selection modal is opened and no template has been chosen yet.
  useEffect(() => {
    if (!getLinkTemplateSelectionModalOpen) return;
    if (!activeEmailTemplates || activeEmailTemplates.length === 0) return;
    if (selectedGetLinkTemplate) return;

    const defaultTemplate = activeEmailTemplates.find((t) => t.default === true);
    if (defaultTemplate) {
      setSelectedGetLinkTemplate(defaultTemplate.id);
    } else if (activeEmailTemplates.length === 1) {
      setSelectedGetLinkTemplate(activeEmailTemplates[0].id);
    }
  }, [
    getLinkTemplateSelectionModalOpen,
    activeEmailTemplates,
    selectedGetLinkTemplate,
  ]);

  // (Area subscription moved into QTDetailsDataFieldsSection)

  // Helper: fire-and-forget call to processAudioWithAuphonicV5 (HTTP onRequest function)
  const fireAndForgetProcessAudio = async ({ fileUrl, qtTitle, ogFileName, collectionName, documentId }) => {
    try {
      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) {
        console.error("No auth token available for background audio processing.");
        return;
      }
      const projectId = auth.app.options.projectId;
      const functionUrl = `https://us-central1-${projectId}.cloudfunctions.net/processAudioWithAuphonicV5`;
      
      fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          fileUrl,
          qtTitle: qtTitle || 'Audio Processing',
          ogFileName: ogFileName || 'audio',
          collectionName,
          documentId,
        }),
      }).catch(err => console.error('Background audio processing error:', err));
      
      console.log("Fire-and-forget audio processing initiated for:", documentId);
    } catch (err) {
      console.error("Error initiating background audio processing:", err);
    }
  };

  // Manual "Process Original Audio" button handler — fires V5 in the background
  const handleProcessOriginalAudio = async () => {
    if (!quicktaleDetails.audioUrl) {
      showNotification("No original audio file to process.");
      return;
    }
    showNotification("Audio processing started in the background.");
    await fireAndForgetProcessAudio({
      fileUrl: quicktaleDetails.audioUrl,
      qtTitle: quicktaleDetails.title,
      ogFileName: quicktaleDetails.originalAudioName,
      collectionName: 'batchQuicktales',
      documentId: quicktaleDetails.id,
    });
  };

  const handleFileUpload = async (file, type, source = "upload") => {
    if (!file) return;

    setLoading(true);
    try {
      let audioUrl;
      let dataTemp = {};

      if (type === "original") {
        const audioUrl = await uploadQuickTaleAudioToStorage(file.raw);
        dataTemp = {
          audioUrl: audioUrl,
          originalAudioName: file.raw.name,
        };
        
        // Create an audio element to get the duration
        const audio = new Audio(audioUrl);
        await new Promise((resolve) => {
          audio.onloadedmetadata = () => {
            const duration = Math.floor(audio.duration); // Duration in seconds
            dataTemp.audioLength = duration;
            setQuicktaleDetails((prev) => ({ 
              ...prev, 
              ...dataTemp,
              audioLength: duration,
              // Clear old processed audio — it belongs to the previous original audio
              processedAudioUrl: '',
              processedAudioName: '',
            }));
            resolve();
          };
        });
        
        // Show notification instead of opening the process audio confirmation modal
        const toastMessage = source === "record"
          ? "Audio recorded successfully! Click Save to update the quicktale."
          : "Audio uploaded successfully! Click Save to update the quicktale.";
        showNotification(toastMessage);
      } else if (type === "processed") {
        const processedAudioUrl = await uploadProcessedAudioToStorage(file.raw);
        dataTemp = {
          processedAudioUrl: processedAudioUrl,
          processedAudioName: file.raw.name,
        };
        
        // Create an audio element to get the duration
        const audio = new Audio(processedAudioUrl);
        await new Promise((resolve) => {
          audio.onloadedmetadata = () => {
            const duration = Math.floor(audio.duration); // Duration in seconds
            dataTemp.audioLength = duration;
            setQuicktaleDetails((prev) => ({ 
              ...prev, 
              ...dataTemp,
              audioLength: duration 
            }));
            resolve();
          };
        });
        
        showNotification(`Processed audio file uploaded successfully.`);
      }
    } catch (error) {
      Toast("error", `Error uploading file: ${error.message}`);
      console.error("Error uploading file:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleImagePreviewModalOpen = () => {
    setImagePreviewModal(true);
  };
  const handleImagePreviewModalClose = () => {
    setImagePreviewModal(false);
  };

  const handleAudioModalOpen = () => {
    setAudioModal(true);
  };
  const handleAudioModalClose = () => {
    setAudioModal(false);
  };

  const handleProcessedAudioModalOpen = () => {
    setProcessedAudioModal(true);
  };
  const handleProcessedAudioModalClose = () => {
    setProcessedAudioModal(false);
  };

  const handleCancelClick = () => {
    setCancelConfirmationModalOpen(true);
  };

  const handleConfirmCancel = () => {
    setCancelConfirmationModalOpen(false);
    setEditEnabled(false);
    // Reset any uploaded files
    setUploadImage(null);
    setUploadAudioFile(null);
    setUploadProcessedAudioFile(null);
    // Refresh the data to remove any unsaved changes by resetting to original state
    setQuicktaleDetails(location.state.quicktaleDetails);
  };

  useEffect(() => {
    const fetchBatchQuickTale = async (id) => {
      console.log("Attempting to fetch Batch QuickTale with ID:", id);
      setLoading(true);
      try {
        const doc = await getSingleBatchQuickTale(id);
        if (doc.exists()) {
          console.log("Batch QuickTale fetched successfully:", doc.id, doc.data());
          const docData = doc.data();
          
          // Resolve createdBy for display. The stored id can point to EITHER a
          // users.id OR an organizations.id (batch imports default to the org id
          // when no Creator is provided). resolveCreatedByData tries users first,
          // then falls back to organizations, tagging the result with _sourceType.
          const resolvedCreatedByData = await resolveCreatedByData(
            docData.createdBy,
            currentOrganization ? [currentOrganization] : null
          );
          const narratorTemp = await getSingleUser(docData.narrator);


          // If narrator is NOT a user (narratorTemp null), attempt to hydrate
          // the custom narrator record so we can display email.
          let customNarratorDocResolved = null;
          try {
            if (DEBUG_CUSTOM_NARRATOR_LOOKUP) {
              console.log("[BatchQuickTaleDetails] narrator resolution entry", {
                batchQuicktaleId: doc.id,
                narratorRaw: docData?.narrator,
                organization: docData?.organization,
                currentUserId: currentUser?.userId,
                currentUid: currentUser?.uid,
                narratorTempHasData: !!narratorTemp?.data,
              });
            }

            if (!narratorTemp?.data && docData?.narrator) {
              const narratorName = String(docData.narrator).trim();
              const userIdToUse = currentUser?.userId || currentUser?.uid;
              const orgId = docData?.organization;

              if (DEBUG_CUSTOM_NARRATOR_LOOKUP) {
                console.groupCollapsed(
                  `[BatchQuickTaleDetails] custom narrator lookup: "${narratorName}"`
                );
                console.log("docData.narrator (raw)", docData?.narrator);
                console.log("narratorName (trimmed)", narratorName);
                console.log("orgId", orgId);
                console.log("userIdToUse", userIdToUse);
                console.log("narratorTemp?.data exists?", !!narratorTemp?.data);
              }

              // 1) Preferred: resolve by org (supports shared narrators)
              if (narratorName && orgId) {
                DEBUG_CUSTOM_NARRATOR_LOOKUP &&
                  console.log("step1: getCustomNarratorByNameAndOrganization", {
                    narratorName,
                    orgId,
                  });
                customNarratorDocResolved = await getCustomNarratorByNameAndOrganization(
                  narratorName,
                  orgId
                );
                DEBUG_CUSTOM_NARRATOR_LOOKUP &&
                  console.log("step1 result", customNarratorDocResolved);
              }

              // 2) Fallback: resolve by owner userId (legacy)
              if (!customNarratorDocResolved && narratorName && userIdToUse) {
                DEBUG_CUSTOM_NARRATOR_LOOKUP &&
                  console.log("step2: getCustomNarratorByName", {
                    narratorName,
                    userIdToUse,
                  });
                customNarratorDocResolved = await getCustomNarratorByName(
                  narratorName,
                  userIdToUse
                );
                DEBUG_CUSTOM_NARRATOR_LOOKUP &&
                  console.log("step2 result", customNarratorDocResolved);
              }

              // 3) Last resort: unscoped lookup by name
              if (!customNarratorDocResolved && narratorName) {
                DEBUG_CUSTOM_NARRATOR_LOOKUP &&
                  console.log("step3: getCustomNarratorByNameUnscoped", {
                    narratorName,
                  });
                customNarratorDocResolved = await getCustomNarratorByNameUnscoped(
                  narratorName
                );
                DEBUG_CUSTOM_NARRATOR_LOOKUP &&
                  console.log("step3 result", customNarratorDocResolved);
              }

              DEBUG_CUSTOM_NARRATOR_LOOKUP && console.groupEnd();
            } else if (DEBUG_CUSTOM_NARRATOR_LOOKUP) {
              console.log(
                "[BatchQuickTaleDetails] custom narrator lookup skipped",
                {
                  reason: !docData?.narrator
                    ? "no narrator field"
                    : narratorTemp?.data
                    ? "narrator resolved as user"
                    : "unknown",
                }
              );
            }
          } catch (e) {
            console.warn("Unable to resolve custom narrator for display", e);
            DEBUG_CUSTOM_NARRATOR_LOOKUP && console.groupEnd();
          }
          
          setQuicktaleDetails({
            id: doc.id,
            ...docData,
            createdByData: resolvedCreatedByData,
            narratorData: narratorTemp?.data() ? narratorTemp.data() : null,

            ...(customNarratorDocResolved
              ? { customNarratorDoc: customNarratorDocResolved }
              : {}),
            // Initialize userNoProcess based on processedAudioUrl
            userNoProcess: docData.processedAudioUrl ? false : true,
          });
          setInitialApprovalStatus(docData?.approvalStatus?.status || null);
        } else {
          console.error("Batch QuickTale not found in DB for ID:", id);
          navigate(-1); // Go back to previous page
        }
      } catch (error) {
        console.error("Error fetching Batch QuickTale from DB:", error);
        navigate(-1); // Go back to previous page
      } finally {
        setLoading(false);
      }
    };
    // Check if we have no data and no valid ID - redirect to parent page
    if (!location?.state?.quicktaleDetails?.id && (!paramBatchQuicktaleId || paramBatchQuicktaleId === ":batchQuicktaleId")) {
      navigate(-1);
      return;
    }

    if (paramBatchQuicktaleId && paramBatchQuicktaleId !== ":batchQuicktaleId" && paramBatchQuicktaleId !== quicktaleDetails.id) {
      fetchBatchQuickTale(paramBatchQuicktaleId);
    } else if (location?.state?.quicktaleDetails) {
      DEBUG_CUSTOM_NARRATOR_LOOKUP &&
        console.log(
          "[BatchQuickTaleDetails] using location.state.quicktaleDetails (no fetchBatchQuickTale)",
          {
            hasId: !!location.state.quicktaleDetails?.id,
            id: location.state.quicktaleDetails?.id,
            narrator: location.state.quicktaleDetails?.narrator,
            hasNarratorData: !!location.state.quicktaleDetails?.narratorData,
          }
        );
      setQuicktaleDetails(location.state.quicktaleDetails);
    }
  }, [paramBatchQuicktaleId, location.state, navigate]);

  // Hydrate the Created By display object even when details were passed via
  // navigation state (which only carries the raw createdBy id, not the resolved
  // createdByData). Without this, the field shows the raw id until a manual
  // refresh triggers fetchBatchQuickTale. The stored id can be a users.id OR an
  // organizations.id; resolveCreatedByData handles both.
  useEffect(() => {
    const hydrateCreatedByForDisplay = async () => {
      try {
        const createdById = quicktaleDetails?.createdBy;
        if (!createdById) return;
        if (quicktaleDetails?.createdByData) return; // Already resolved

        const resolved = await resolveCreatedByData(
          createdById,
          currentOrganization ? [currentOrganization] : null
        );
        if (resolved) {
          setQuicktaleDetails((prev) => ({ ...prev, createdByData: resolved }));
        }
      } catch (e) {
        console.warn("hydrateCreatedByForDisplay failed", e);
      }
    };

    hydrateCreatedByForDisplay();
  }, [
    quicktaleDetails?.createdBy,
    quicktaleDetails?.createdByData,
    currentOrganization,
  ]);

  // Hydrate custom narrator email even when details were passed via navigation state.
  useEffect(() => {
    const hydrateCustomNarratorForDisplay = async () => {

      try {
        const narratorRaw = quicktaleDetails?.narrator;
        const narratorName = String(narratorRaw || "").trim();
        const isUserNarrator = !!quicktaleDetails?.narratorData;
        const alreadyHydrated = !!quicktaleDetails?.customNarratorDoc;

        if (!narratorName || isUserNarrator || alreadyHydrated) return;

        const userIdToUse = currentUser?.userId || currentUser?.uid;
        const orgId = quicktaleDetails?.organization;

        DEBUG_CUSTOM_NARRATOR_LOOKUP &&
          console.groupCollapsed(
            `[BatchQuickTaleDetails] hydrate custom narrator (state path): "${narratorName}"`
          );
        DEBUG_CUSTOM_NARRATOR_LOOKUP &&
          console.log({
            narratorRaw,
            narratorName,
            orgId,
            userIdToUse,
            isUserNarrator,
            alreadyHydrated,
          });

        let cn = null;
        if (narratorName && orgId) {
          DEBUG_CUSTOM_NARRATOR_LOOKUP &&
            console.log("state step1: getCustomNarratorByNameAndOrganization", {
              narratorName,
              orgId,
            });
          cn = await getCustomNarratorByNameAndOrganization(narratorName, orgId);
          DEBUG_CUSTOM_NARRATOR_LOOKUP && console.log("state step1 result", cn);
        }
        if (!cn && narratorName && userIdToUse) {
          DEBUG_CUSTOM_NARRATOR_LOOKUP &&
            console.log("state step2: getCustomNarratorByName", {
              narratorName,
              userIdToUse,
            });
          cn = await getCustomNarratorByName(narratorName, userIdToUse);
          DEBUG_CUSTOM_NARRATOR_LOOKUP && console.log("state step2 result", cn);
        }
        if (!cn && narratorName) {
          DEBUG_CUSTOM_NARRATOR_LOOKUP &&
            console.log("state step3: getCustomNarratorByNameUnscoped", {
              narratorName,
            });
          cn = await getCustomNarratorByNameUnscoped(narratorName);
          DEBUG_CUSTOM_NARRATOR_LOOKUP && console.log("state step3 result", cn);
        }

        if (cn) {
          setQuicktaleDetails((prev) => ({
            ...prev,
            customNarratorDoc: cn,
          }));
          DEBUG_CUSTOM_NARRATOR_LOOKUP &&
            console.log("state hydrate success -> setQuicktaleDetails.customNarratorDoc", cn);
        } else {
          DEBUG_CUSTOM_NARRATOR_LOOKUP &&
            console.log("state hydrate: no custom narrator record found");
        }

        DEBUG_CUSTOM_NARRATOR_LOOKUP && console.groupEnd();
      } catch (e) {
        console.warn("hydrateCustomNarratorForDisplay failed", e);
        DEBUG_CUSTOM_NARRATOR_LOOKUP && console.groupEnd();
      }
    };

    hydrateCustomNarratorForDisplay();
  }, [
    quicktaleDetails?.narrator,
    quicktaleDetails?.narratorData,
    quicktaleDetails?.customNarratorDoc,
    quicktaleDetails?.organization,
    currentUser?.userId,
    currentUser?.uid,
  ]);

  // Don't render anything if we don't have valid quicktale data
    if (!quicktaleDetails?.id) {
      return null;
    }

  const isApproved = quicktaleDetails?.approvalStatus?.status === "approved";
  // Lock the UI only after the record has actually been promoted.
  // This allows the user to change QT Org Status to Approved and still click Save.
  const isLocked = isApproved && initialApprovalStatus === "approved";

  const sendLinkToNarrator = async (email, firstName, lastName, templateId) => {
    setLoading(true);
    try {
      const sendExternalUpdateLink = httpsCallable(functions, 'sendExternalUpdateLink');
      await sendExternalUpdateLink({
        quicktaleId: quicktaleDetails.id,
        email: email,
        firstName: firstName,
        lastName: lastName,
        quickTale: {
          title: quicktaleDetails.title,
        },
        origin: window.location.origin,
        emailTemplateId: templateId,
        orgName: currentOrganization?.name || "",
      });
      showNotification("Update link sent successfully.");
    } catch (error) {
      Toast("error", `Error sending link: ${error.message}`);
      console.error("Error calling sendExternalUpdateLink:", error);
    } finally {
      setLoading(false);
      // Reset template selection
      setSelectedTemplate("");
      setPendingNarratorData(null);
    }
  };

  const handleSendLink = async () => {
    // Case 1: Narrator is a registered user with email
    if (!quicktaleDetails.narrator) {
      showNotification("Told By is required to send a link.");
      return;
    }
    if (quicktaleDetails.narratorData && quicktaleDetails.narratorData.email) {
      // Store narrator data and open template selection modal
      setPendingNarratorData({
        email: quicktaleDetails.narratorData.email,
        firstName: quicktaleDetails.narratorData.firstName,
        lastName: quicktaleDetails.narratorData.lastName,
      });
      // Auto-select if only one template
      if (activeEmailTemplates.length === 1) {
        setSelectedTemplate(activeEmailTemplates[0].id);
      }
      setTemplateSelectionModalOpen(true);
      return;
    }

    // Case 2: Narrator is a custom narrator
    if (!quicktaleDetails.narratorData && quicktaleDetails.narrator) {
      const narratorName = quicktaleDetails.narrator.trim();
      const userIdToUse = currentUser?.userId || currentUser?.uid;
      
      try {
        const customNarrator = await getCustomNarratorByName(
          narratorName,
          userIdToUse
        );

        // Always prompt for narrator details so First Name, Last Name, and
        // Email Address are visible and editable, regardless of whether
        // values already exist.
        setCustomNarratorDoc(customNarrator);
        setNarratorEmail(customNarrator?.email || "");
        setNarratorFirstName(customNarrator?.firstName || "");
        setNarratorLastName(customNarrator?.lastName || "");
        setEmailPromptModalOpen(true);
      } catch (error) {
        Toast("error", "Could not find custom narrator.");
        console.error("Error fetching custom narrator:", error);
      }
      return;
    }

    Toast("error", "Narrator has no email address.");
  };

  // Handle template selection modal continue
  const handleTemplateSelectionContinue = async () => {
    if (!selectedTemplate) {
      showNotification("Please select an email template.");
      return;
    }
    
    setTemplateSelectionModalOpen(false);
    
    if (pendingNarratorData) {
      await sendLinkToNarrator(
        pendingNarratorData.email,
        pendingNarratorData.firstName,
        pendingNarratorData.lastName,
        selectedTemplate
      );
    }
  };

  // Handle template selection modal close
  const handleTemplateSelectionClose = () => {
    setTemplateSelectionModalOpen(false);
    setSelectedTemplate("");
    setPendingNarratorData(null);
  };

  const handleEmailPromptContinue = async () => {
    // Validate all required fields
    if (!narratorFirstName) {
      showNotification("Please enter a first name.");
      return;
    }
    if (!narratorEmail) {
      showNotification("Please enter an email address.");
      return;
    }
    // Validate template selection (if templates are available)
    if (activeEmailTemplates.length > 0 && !selectedTemplate) {
      showNotification("Please select an email template.");
      return;
    }
    
    setEmailPromptModalOpen(false);

    try {
      // Update the custom narrator record with all the new details
      if (customNarratorDoc && customNarratorDoc.id) {
        await updateCustomNarrator(customNarratorDoc.id, { 
          email: narratorEmail,
          firstName: narratorFirstName,
          lastName: narratorLastName,
          ...(customNarratorOrganizationId
            ? { organization: customNarratorOrganizationId }
            : {}),
        });
        await sendLinkToNarrator(narratorEmail, narratorFirstName, narratorLastName, selectedTemplate);
      } else {
        throw new Error("Custom narrator document not found.");
      }
    } catch (error) {
      Toast("error", `Failed to save narrator details: ${error.message}`);
      console.error("Error updating custom narrator:", error);
    }
    // Reset all inputs
    setNarratorEmail("");
    setNarratorFirstName("");
    setNarratorLastName("");
    setSelectedTemplate("");
  };
  
  // Handle email prompt modal close
  const handleEmailPromptClose = () => {
    setEmailPromptModalOpen(false);
    setNarratorEmail("");
    setNarratorFirstName("");
    setNarratorLastName("");
    setSelectedTemplate("");
    setCustomNarratorDoc(null);
  };

  const handleGetLink = async () => {
    setIsCopied(false); // Reset copied state when opening modal

    // Templates are still being fetched — don't show the "no templates" error yet
    if (templatesLoading) return;

    if (!activeEmailTemplates || activeEmailTemplates.length === 0) {
      showNotification("Please create an email template");
      return;
    }

    // If there is only one active template, pre-select it
    if (activeEmailTemplates.length === 1) {
      setSelectedGetLinkTemplate(activeEmailTemplates[0].id);
    }

    setGetLinkTemplateSelectionModalOpen(true);
  };

  const handleGetLinkTemplateSelectionContinue = async () => {
    if (!selectedGetLinkTemplate) {
      showNotification("Please select an email template.");
      return;
    }

    setGetLinkTemplateSelectionModalOpen(false);
    setLoading(true);

    try {
      // Resolve narrator info to pass to the cloud function for activity logging
      const getNarratorInfoForGetLink = () => {
        if (quicktaleDetails.narratorData) {
          return {
            narratorName: `${quicktaleDetails.narratorData.firstName || ''} ${quicktaleDetails.narratorData.lastName || ''}`.trim(),
            narratorEmail: quicktaleDetails.narratorData.email || '',
          };
        }
        if (quicktaleDetails.customNarratorDoc) {
          return {
            narratorName: quicktaleDetails.customNarratorDoc.name || quicktaleDetails.narrator || '',
            narratorEmail: quicktaleDetails.customNarratorDoc.email || '',
          };
        }
        return {
          narratorName: quicktaleDetails.narrator || '',
          narratorEmail: '',
        };
      };
      const narratorInfo = getNarratorInfoForGetLink();

      const generateExternalUpdateLink = httpsCallable(functions, 'generateExternalUpdateLink');
      const result = await generateExternalUpdateLink({
        quicktaleId: quicktaleDetails.id,
        origin: window.location.origin,
        collection: 'batchQuicktales',
        emailTemplateId: selectedGetLinkTemplate,
        narratorName: narratorInfo.narratorName,
        narratorEmail: narratorInfo.narratorEmail,
      });

      // Best effort: if this is a custom narrator and we can resolve the doc,
      // persist organization so the narrator page can resolve tokens by org.
      try {
        const narratorName = (quicktaleDetails?.narrator || '').trim();
        if (narratorName && !quicktaleDetails?.narratorData && customNarratorOrganizationId) {
          const userIdToUse = currentUser?.userId || currentUser?.uid;
          const cn = await getCustomNarratorByName(narratorName, userIdToUse);
          if (cn?.id && !cn?.organization) {
            await updateCustomNarrator(cn.id, { organization: customNarratorOrganizationId });
          }
        }
      } catch (e) {
        console.warn('Get Link: unable to upsert customNarrator.organization', e);
      }

      const { link } = result.data;
      if (link) {
        setGeneratedLink(link);
        setGetLinkModalOpen(true);
      } else {
        throw new Error("Could not generate link.");
      }
    } catch (error) {
      Toast("error", `Error generating link: ${error.message}`);
      console.error("Error calling generateExternalUpdateLink:", error);
    } finally {
      setLoading(false);
      setSelectedGetLinkTemplate("");
    }
  };

  const handleGetLinkTemplateSelectionClose = () => {
    setGetLinkTemplateSelectionModalOpen(false);
    setSelectedGetLinkTemplate("");
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(generatedLink);
    setIsCopied(true);
    // Keep modal open, but show "Copied!" text
    setTimeout(() => {
        setGetLinkModalOpen(false);
        setIsCopied(false); // Reset for next time
    }, 1500); // Close modal after 1.5s
  };


  const handleUpdateQuickTale = async () => {
    if (!quicktaleDetails.narrator) {
      setNarratorError(true);
      showNotification("Told by is a required field.");
      return;
    } else {
      setNarratorError(false);
    }
    try {
      setLoading(true);
      isSaving.current = true;
      
      // Check if narrator is a custom name (text) and not a user ID
      // If narratorData is null, it means they typed text or selected a custom narrator
      let customNarratorCreated = false;
      if (!quicktaleDetails.narratorData && quicktaleDetails.narrator) {
        const narratorName = quicktaleDetails.narrator.trim();
        
        // Get user ID to check/create custom narrators
        const userIdToUse = currentUser?.userId || currentUser?.uid;
        
        if (userIdToUse && narratorName) {
          try {
            // Fetch current custom narrators for this user
            const existingNarrators = await getCustomNarratorsByUser(userIdToUse);
            
            // Check if this narrator name already exists
            if (!narratorNameExists(narratorName, existingNarrators)) {
              // Create new custom narrator
              const newNarrator = await createCustomNarrator(narratorName, userIdToUse);
              if (newNarrator) {
                console.log("Created new custom narrator:", newNarrator);
                customNarratorCreated = true;
              }
            }
          } catch (error) {
            console.error("Error checking/creating custom narrator:", error);
            // Continue with save even if custom narrator creation fails
          }
        }
      }

      let title =
        quicktaleDetails.title || `Untitled ${moment().format("MM-DD-YYYY")}`;
      setQuicktaleDetails({ ...quicktaleDetails, title: title });
      let dataTemp = {
        title: title,
        description: quicktaleDetails?.description || "",
        status: quicktaleDetails?.status || "",
        areaIdResolved: quicktaleDetails?.areaIdResolved || "",
        areaNameFromCsv: quicktaleDetails?.areaNameFromCsv || "",
        isDraft:
          quicktaleDetails?.isDraft !== undefined
            ? quicktaleDetails.isDraft
            : quicktaleDetails?.title?.includes("Untitled") || false,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.id || "",
        createdBy: quicktaleDetails?.createdBy || "",
        narrator: quicktaleDetails?.narrator || "",
        location: quicktaleDetails?.location || "",
        locationDisplay: quicktaleDetails?.locationDisplay || "",
        locationCountry: quicktaleDetails?.locationCountry || "",
        coordinates: new GeoPoint(
          quicktaleDetails?.coordinates?._lat || 0,
          quicktaleDetails?.coordinates?._long || 0
        ),
        g: {
          geohash: quicktaleDetails?.g?.geohash || "",
          geopoint: new GeoPoint(
            quicktaleDetails?.coordinates?._lat || 0,
            quicktaleDetails?.coordinates?._long || 0
          ),
        },
      };

      // Persist approvalStatus if present
      if (quicktaleDetails.approvalStatus) {
        // Always keep approvalStatus.updatedAt in Firestore Timestamp format.
        dataTemp.approvalStatus = {
          ...quicktaleDetails.approvalStatus,
          updatedAt: serverTimestamp(),
        };
      }
      if (quicktaleDetails.tags !== location?.state?.quicktaleDetails?.tags) {
        if (typeof quicktaleDetails.tags === "string") {
          dataTemp.tags = quicktaleDetails.tags.split(",");
        } else if (Array.isArray(quicktaleDetails.tags)) {
          dataTemp.tags = [...quicktaleDetails.tags];
        } else {
          dataTemp.tags = [];
        }
      }

      if (uploadImage != null) {
        let photoUrl = await uploadQuickTaleImageToStorage(uploadImage?.raw);
        dataTemp.imageUrls = [photoUrl];
        dataTemp.originalPhotoName = uploadImage?.raw?.name;
      }
      if (quicktaleDetails.audioUrl) {
        dataTemp.audioUrl = quicktaleDetails.audioUrl;
        dataTemp.originalAudioName = quicktaleDetails.originalAudioName;
      }
      if (quicktaleDetails.processedAudioUrl) {
        dataTemp.processedAudioUrl = quicktaleDetails.processedAudioUrl;
      } else {
        dataTemp.processedAudioUrl = '';
      }
      if (quicktaleDetails.processedAudioName) {
        dataTemp.processedAudioName = quicktaleDetails.processedAudioName;
      } else {
        dataTemp.processedAudioName = '';
      }
      if (quicktaleDetails.audioLength) {
        dataTemp.audioLength = quicktaleDetails.audioLength;
      }
      // Save userNoProcess field
      if (quicktaleDetails.userNoProcess !== undefined) {
        dataTemp.userNoProcess = quicktaleDetails.userNoProcess;
      }
      // Save autoApprove flag
      dataTemp.autoApprove = quicktaleDetails.autoApprove === true;
      console.log("data:", dataTemp);
      await updateBatchQuickTale(quicktaleDetails.id, dataTemp);
      
      // If status just transitioned to approved, promote this Batch QuickTale
      if (
        initialApprovalStatus !== "approved" &&
        quicktaleDetails?.approvalStatus?.status === "approved"
      ) {
        try {
          await promoteBatchQuicktale({ batchQuicktaleId: quicktaleDetails.id });
          showNotification("QuickTale(s) added to QuickTales in Organization.");
          setInitialApprovalStatus("approved");
        } catch (promoError) {
          Toast("error", `Error adding QuickTale: ${promoError.message}`);
          console.error("Error adding QuickTale:", promoError);
        }
      } else {
        showNotification("Batch QuickTale updated successfully.");
      }
      
      // Note: We don't update AddSearch or Clarifai directly from the client
      // for batch quicktales; promotion function handles that.

      // Fire-and-forget: process audio in the background via V5
      // Triggers when: (a) there's an audioUrl but no processedAudioUrl, OR
      //                (b) new audio was uploaded/recorded in this edit session
      if (quicktaleDetails.audioUrl && (!quicktaleDetails.processedAudioUrl || uploadAudioFile != null)) {
        console.log("Initiating background audio processing via processAudioWithAuphonicV5");
        fireAndForgetProcessAudio({
          fileUrl: quicktaleDetails.audioUrl,
          qtTitle: title,
          ogFileName: quicktaleDetails.originalAudioName,
          collectionName: 'batchQuicktales',
          documentId: quicktaleDetails.id,
        });
      }
      
    } catch (error) {
      Toast("error", error.message);
      console.log("error updating batch quicktale", error.message);
    } finally {
      setLoading(false);
      setEditEnabled(false);
    }
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <DetailsPageHeader
        mainTitle={
          quicktaleDetails && quicktaleDetails.title && quicktaleDetails.title
        }
        bgColor="#fff"
        buttonText={"Edit"}
        editEnabled={editEnabled}
        setEditEnabled={isLocked ? () => {} : setEditEnabled}
        cancelButton={editEnabled ? true : false}
        onCancelClick={handleCancelClick}
        handleSave={() => handleUpdateQuickTale()}
        loading={loading}
        showSendLink={!editEnabled}
        onSendLink={isLocked ? () => {} : handleSendLink}
        showGetLink={!editEnabled}
        onGetLink={isLocked ? () => {} : handleGetLink}
        hideBackArrow={editEnabled}
        disableEdit={isLocked}
        disableSendLink={isLocked}
        disableGetLink={isLocked}
      />
      <Sidebar />
      <Grid container style={{ ...mainLayoutStyles, background: "#fff" }}>
        {isLocked && (
          <Grid
            item
            xs={12}
            sx={{
              paddingLeft: 4,
              paddingRight: 4,
              marginTop: 2,
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            <span style={{ color: "#00A7CD", fontSize: 18, textAlign: "center" }}>
              This{" "}
              <span
                style={{ textDecoration: "underline", cursor: "pointer"}}
                onClick={() =>
                  navigate(
                    routes.quicktaleInOrgDetails.replace(
                      ":quicktaleId",
                      quicktaleDetails.id
                    )
                  )
                }
              >
                QuickTale
              </span>{" "}
              has been approved. To modify, go to QuickTales in Organization.
            </span>
          </Grid>
        )}
        <Grid
          container
          direction={"column"}
          style={isLocked ? { ...subContainerStyles } : subContainerStyles}
        >
          {/*Data card/container section*/}
          <QTDetailsDataFieldsSection
            quicktaleDetails={quicktaleDetails}
            setQuicktaleDetails={setQuicktaleDetails}
            isSaving={isSaving}
            handleImagePreviewModalOpen={handleImagePreviewModalOpen}
            handleAudioModalOpen={handleAudioModalOpen}
            handleProcessedAudioModalOpen={handleProcessedAudioModalOpen}
            editEnabled={editEnabled}
            uploadImage={uploadImage}
            setUploadImage={setUploadImage}
            uploadAudioFile={uploadAudioFile}
            setUploadAudioFile={setUploadAudioFile}
            uploadProcessedAudioFile={uploadProcessedAudioFile}
            setUploadProcessedAudioFile={setUploadProcessedAudioFile}
            handleFileUpload={handleFileUpload}
            onProcessOriginalAudio={handleProcessOriginalAudio}
            narratorError={narratorError}
            areasMode={"batchSingle"}
            showAutoApprove={true}
            autoApprove={!!quicktaleDetails?.autoApprove}
            onAutoApproveChange={(e) =>
              setQuicktaleDetails((prev) => ({
                ...prev,
                autoApprove: e.target.checked,
              }))
            }
          />

          {/* Import messages (read-only) */}
          <Grid
            container
            direction={"column"}
            sx={{
              mt: 4,
              pt: 3,
              borderTop: "1px solid #e0e0e0",
            }}
          >
            <h3 style={{ margin: 0, marginBottom: 12, fontFamily: "Avenir Book, sans-serif" }}>
              Import Messages
            </h3>

            {/* Row 1: Photo / Audio / Title */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <QTDetailsTextFieldWithLabelSection
                  label={"Photo"}
                  value={quicktaleDetails?.msgPhoto || ""}
                  disabled={true}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <QTDetailsTextFieldWithLabelSection
                  label={"Audio"}
                  value={quicktaleDetails?.msgAudio || ""}
                  disabled={true}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <QTDetailsTextFieldWithLabelSection
                  label={"Title"}
                  value={quicktaleDetails?.msgTitle || ""}
                  disabled={true}
                />
              </Grid>
            </Grid>

            {/* Row 2: Narrator / Other */}
            <Grid container spacing={2} sx={{ }}>
              <Grid item xs={12} md={4}>
                <QTDetailsTextFieldWithLabelSection
                  label={"Narrator"}
                  value={quicktaleDetails?.msgNarrator || ""}
                  disabled={true}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <QTDetailsTextFieldWithLabelSection
                  label={"Other"}
                  value={quicktaleDetails?.msgOther || ""}
                  disabled={true}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Share Activity section */}
          <Grid
            container
            direction={"column"}
            sx={{
              mt: 4,
              pt: 3,
              borderTop: "1px solid #e0e0e0",
            }}
          >
            <h3 style={{ margin: 0, marginBottom: 12, fontFamily: "Avenir Book, sans-serif" }}>
              Contributor Request Activity
            </h3>
            <ShareActivityTable
              events={shareActivityEvents}
              loading={shareActivityLoading}
              templates={allEmailTemplates}
            />
          </Grid>
          <div style={{marginBottom: 100}}></div>
        </Grid>
      </Grid>
      <ImagePreviewModal
        modalOpen={imagePreviewModal}
        handleClose={handleImagePreviewModalClose}
        image={
          uploadImage != null && uploadImage.preview
            ? uploadImage.preview
            : quicktaleDetails.photoUrl
        }
      />
      <AudioModal
        modalOpen={audioModal}
        handleClose={handleAudioModalClose}
        audioFile={
          uploadAudioFile != null && uploadAudioFile.preview
            ? uploadAudioFile.preview
            : quicktaleDetails.audioUrl
        }
      />
      <AudioModal
        modalOpen={processedAudioModal}
        handleClose={handleProcessedAudioModalClose}
        audioFile={
          uploadProcessedAudioFile != null && uploadProcessedAudioFile.preview
            ? uploadProcessedAudioFile.preview
            : quicktaleDetails.processedAudioUrl
        }
      />
      <ConfirmationModal
        open={cancelConfirmationModalOpen}
        onClose={() => setCancelConfirmationModalOpen(false)}
        onConfirm={handleConfirmCancel}
        title="Discard Changes"
        message="Are you sure you want to cancel? All unsaved changes will be lost."
      />
      {/* Template Selection Modal - for when narrator already has email */}
      <ConfirmationModal
        open={templateSelectionModalOpen}
        onClose={handleTemplateSelectionClose}
        onConfirm={handleTemplateSelectionContinue}
        title="Select Email Template"
        message="Please select a template for your email."
        cancelButtonText="Cancel"
        confirmButtonText="Continue"
        showTemplateDropdown={true}
        templates={activeEmailTemplates}
        selectedTemplate={selectedTemplate}
        onTemplateChange={(e) => setSelectedTemplate(e.target.value)}
        noTemplatesMessage="Please create an email template"
        // When narrator is a registered user, show their details as read-only
        narratorName={
          quicktaleDetails.narratorData
            ? quicktaleDetails.narratorData.userName
              ? `@${quicktaleDetails.narratorData.userName}`
              : `${quicktaleDetails.narratorData.firstName || ""} ${
                  quicktaleDetails.narratorData.lastName || ""
                }`.trim()
            : undefined
        }
        firstName={quicktaleDetails.narratorData?.firstName}
        lastName={quicktaleDetails.narratorData?.lastName}
        textFieldValue={quicktaleDetails.narratorData?.email}
        narratorReadOnly={!!quicktaleDetails.narratorData}
      />
      {/* Email Prompt Modal - for when narrator is missing details (with template dropdown) */}
      <ConfirmationModal
        open={emailPromptModalOpen}
        onClose={handleEmailPromptClose}
        onConfirm={handleEmailPromptContinue}
        title="Narrator Details Required"
        message="Please enter the missing details needed to send the update link."
        textFieldValue={narratorEmail}
        onTextFieldChange={(e) => setNarratorEmail(e.target.value)}
        cancelButtonText="Cancel"
        confirmButtonText="Continue"
        narratorName={customNarratorDoc?.name || quicktaleDetails.narrator}
        firstName={narratorFirstName}
        onFirstNameChange={(e) => setNarratorFirstName(e.target.value)}
        lastName={narratorLastName}
        onLastNameChange={(e) => setNarratorLastName(e.target.value)}
        showTemplateDropdown={true}
        templates={activeEmailTemplates}
        selectedTemplate={selectedTemplate}
        onTemplateChange={(e) => setSelectedTemplate(e.target.value)}
        noTemplatesMessage="Please create an email template"
      />

      {/* Template Selection Modal - for Get Link (link-only, no send) */}
      <ConfirmationModal
        open={getLinkTemplateSelectionModalOpen}
        onClose={handleGetLinkTemplateSelectionClose}
        onConfirm={handleGetLinkTemplateSelectionContinue}
        title="Select Email Template"
        message="Please select a template for the narrator page settings."
        cancelButtonText="Cancel"
        confirmButtonText="Continue"
        showTemplateDropdown={true}
        templates={activeEmailTemplates}
        selectedTemplate={selectedGetLinkTemplate}
        onTemplateChange={(e) => setSelectedGetLinkTemplate(e.target.value)}
        noTemplatesMessage="Please create an email template"
      />

      <ConfirmationModal
        open={getLinkModalOpen}
        onClose={() => setGetLinkModalOpen(false)}
        onConfirm={handleCopyToClipboard}
        title="Link to update QuickTale photo, audio, title, and/or description"
        message="" // Message is now inside the text field
        confirmButtonText={isCopied ? "Copied!" : "Copy"}
        cancelButtonText="Close"
        showTextField={true}
        textFieldValue={generatedLink}
      />
    </Box>
  );
};

export default BatchQuickTaleDetailsContainer;

const subContainerStyles = {
  paddingLeft: 30,
  paddingRight: 30,
  marginTop: 50,
};
